/**
 * Les créneaux libres d'une journée.
 *
 * ⭐ Fonction PURE, en minutes depuis minuit : ni `Date`, ni fuseau, ni
 * localisation. Un rendez-vous se prend à l'horloge murale de la boutique —
 * « jeudi 14 h » veut dire 14 h sur place, quel que soit le poste qui consulte.
 * Mêler des instants au calcul ferait entrer le fuseau du navigateur dans une
 * question qui ne le concerne pas, et déplacerait les créneaux d'une heure deux
 * fois par an. La conversion en instants appartient à l'appelant, qui connaît
 * le fuseau de la boutique.
 *
 * ⚠️ Se relit facilement à l'envers : ce fichier ne sait pas ce qu'est un
 * client, une vente ni un article. Il répond à une seule question — « quels
 * départs possibles, sachant les heures d'ouverture, une durée, et ce qui est
 * déjà pris ? »
 */
/** « 09:30 » → 570. */
export function enMinutes(heure) {
    const [h, m] = heure.split(':');
    return Number(h) * 60 + Number(m);
}
/** 570 → « 09:30 ». */
export function enHeure(minutes) {
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}
/**
 * Deux intervalles se chevauchent-ils ?
 *
 * ⚠️ Bornes STRICTES : un rendez-vous qui finit à 10 h et un autre qui commence
 * à 10 h ne se chevauchent pas. Avec `<=`, la moitié des créneaux d'une journée
 * pleine disparaîtraient sans raison.
 */
function chevauche(aDebut, aFin, bDebut, bFin) {
    return aDebut < bFin && bDebut < aFin;
}
/** Les départs possibles dans UNE plage d'ouverture. */
function creneauxDeLaPlage(plage, duree, pas, occupes, plancher) {
    const ouverture = enMinutes(plage.start);
    const fermeture = enMinutes(plage.end);
    const creneaux = [];
    // ⚠️ Le créneau doit tenir ENTIÈREMENT dans la plage : proposer 17 h 45 pour
    // une prestation d'une heure dans une boutique qui ferme à 18 h, c'est
    // promettre au client un quart d'heure de service et trois quarts d'heure
    // devant une porte close.
    for (let debut = ouverture; debut + duree <= fermeture; debut += pas) {
        if (debut < plancher)
            continue;
        const fin = debut + duree;
        if (occupes.some((o) => chevauche(debut, fin, o.debut, o.fin)))
            continue;
        creneaux.push({ start: enHeure(debut), end: enHeure(fin) });
    }
    return creneaux;
}
/**
 * Les créneaux libres d'une journée, dans l'ordre.
 *
 * Renvoie une liste vide pour un jour fermé, une durée absurde, ou une journée
 * entièrement réservée — trois situations distinctes qui, vues du comptoir, se
 * disent d'une seule manière : « rien ce jour-là ».
 */
export function creneauxLibres(options) {
    const { horaires, jourSemaine, duree, occupes = [], maintenant } = options;
    if (duree <= 0)
        return [];
    const journee = horaires.days[jourSemaine];
    if (!journee || journee.closed)
        return [];
    const plancher = maintenant ?? -1;
    return journee.ranges.flatMap((plage) => creneauxDeLaPlage(plage, duree, horaires.slotMinutes, occupes, plancher));
}
/**
 * La boutique est-elle ouverte à cette heure-là ?
 *
 * Sert à valider un rendez-vous posé À LA MAIN — décalé pour arranger un
 * client, par exemple. Le calcul des créneaux propose ; celui-ci contrôle.
 */
export function dansLesHoraires(horaires, jourSemaine, debut, fin) {
    const journee = horaires.days[jourSemaine];
    if (!journee || journee.closed)
        return false;
    return journee.ranges.some((p) => debut >= enMinutes(p.start) && fin <= enMinutes(p.end));
}
/**
 * Combien de postes sont bloqués sur TOUT l'intervalle [debut, fin[ ?
 *
 * ⚠️ On retient le MAXIMUM rencontré, pas la somme ni la valeur à l'instant du
 * départ. Un rendez-vous d'une heure qui commence à 9 h alors que deux postes
 * ferment à 9 h 30 doit compter ces deux postes : la prestation déborde sur la
 * fermeture, et promettre un fauteuil qui n'existera plus dans trente minutes
 * est exactement ce qu'un planning ne doit pas faire.
 *
 * ⚠️ Deux blocages qui se chevauchent s'ADDITIONNENT — « 1 poste pour la
 * formation » et « 1 poste pour la maintenance » font deux postes fermés. Les
 * traiter comme un maximum aurait laissé passer une réservation sur une place
 * qui n'existe pas.
 */
export function postesBloques(blocages, debut, fin) {
    // Les instants où la somme peut changer : chaque début de blocage, plus le
    // début de la prestation. Entre deux bornes, la somme est constante.
    const bornes = [debut, ...blocages.map((b) => b.debut).filter((t) => t > debut && t < fin)];
    let pire = 0;
    for (const t of bornes) {
        const somme = blocages
            .filter((b) => b.debut <= t && t < b.fin)
            .reduce((total, b) => total + b.postes, 0);
        if (somme > pire)
            pire = somme;
    }
    return pire;
}
/**
 * QUELLES cases d'une colonne porter en « fermé », une fois qu'on sait combien
 * de places le sont.
 *
 * ⭐ Un blocage dit COMBIEN de places ferment, jamais LESQUELLES : les postes
 * sont interchangeables, et nommer un fauteuil obligerait à réaffecter les
 * rendez-vous posés dessus pour libérer celui qu'on vient de fermer. Une grille
 * doit pourtant bien hachurer des cases — cette fonction tranche, et elle seule,
 * pour que l'écran et le calcul de disponibilité ne divergent pas.
 *
 * ⚠️ JAMAIS une place occupée. Un rendez-vous déjà pris sur une place qu'on
 * ferme ensuite reste un engagement envers un client : au planning de continuer
 * à le montrer, à la boutique de l'appeler. Le recouvrir d'une hachure l'aurait
 * fait disparaître de l'écran sans l'annuler en base.
 *
 * ⚠️ Les DERNIÈRES places libres, et non les premières : la première colonne
 * reste celle qu'on remplit, donc celle qu'on lit. Fermer par la fin laisse la
 * grille se vider dans l'ordre où elle se remplit.
 *
 * @param places identifiants des postes, dans l'ordre d'affichage.
 * @param occupees places déjà prises par un rendez-vous.
 * @param fermes nombre de places fermées, tel que `postesBloques` le rend.
 */
export function placesFermees(places, occupees, fermes) {
    if (fermes <= 0)
        return new Set();
    const libres = places.filter((p) => !occupees.has(p));
    return new Set(libres.slice(Math.max(libres.length - fermes, 0)));
}
//# sourceMappingURL=creneaux.js.map